import axios from 'axios';
import docReady from '../../../assets/js/docReady';
import { loadOrderFlow, loadSignupFlow, registerOrderFlowButtons } from '../../../assets/js/helpers/orderFlow';

class DynamicEnergyComparison {
    constructor(element) {
        this.element = element;

        this.productContainerElement = null;
        this.location = null;

        this.filter = {
            gas: 1100,
            electricity: 1250,
            electricity_off_peak: 1250,
            electricity_double_meter: 'true',
            providers: [],
            contract_types: 'dynamic',
            include_gift_value: false,
            group_provider_products: false,
            sort: 'price',
            price_per_year: false,
        };

        this.filterToggleElement = null;
        this.filterBarElement = null;
    }

    bootstrap() {
        this.productContainerElement = this.element.querySelector('.products-container');
        this.filterBarElement = this.element.querySelector('.filter-container');
        this.filterToggleElement = this.element.querySelector('#showFilterToggle');
    }

    addEventListeners() {
        document.addEventListener('flowDataReady', (event) => {
            this.updateFlowData(event.detail);
            this.logEvent('updated-products', {});
        });

        this.filterToggleElement.addEventListener('change', (event) => {
            document.body.classList.toggle('no-scroll', event.target.checked);
        });

        this.filterBarElement.addEventListener('change', (event) => {
            const { target } = event;
            const desktop = window.innerWidth >= 992;
            let render = false;
            if (target.name === 'providers') {
                const checkboxes = this.filterBarElement.querySelectorAll('input[name="providers"]:checked');
                this.filter.providers = Array.from(checkboxes)
                    .map((checkbox) => checkbox.id);
                render = true;
                this.logEvent('filter-providers', { value: this.filter.providers });
            } else if (target.type === 'checkbox') {
                const {
                    name,
                    checked,
                } = target;
                this.filter[name] = checked;
                render = true;
                this.logEvent(`filter-${name}`, { value: checked });
            } else {
                const {
                    name,
                    value,
                } = target;
                if (target.type === 'radio' || target.type === 'select-one') {
                    this.filter[name] = value;
                    render = true;
                    this.logEvent(`filter-${name}`, { value });
                }
            }
            if (render && desktop) {
                this.renderProducts(true);
            }
        });

        // Event listeners
        this.element.addEventListener('change', (event) => {
            if (event.target.matches('#showTip')) {
                if (event.target.checked) {
                    this.logEvent('show-tip', {});
                } else {
                    this.logEvent('hide-tip', {});
                }
            } else if (event.target.matches('input[type="checkbox"][name="pricing"]')) {
                if (event.target.checked) {
                    this.logEvent('show-pricing', {});
                } else {
                    this.logEvent('hide-pricing', {});
                }
            } else if (event.target.matches('input[type="checkbox"]#showFilterToggle')) {
                if (event.target.checked) {
                    this.logEvent('show-filters', {});
                } else {
                    this.renderProducts(true);
                    this.logEvent('hide-filters', {});
                }
            } else if (event.target.matches('input[type="checkbox"][name="full-tariffs"]') && event.target.checked) {
                this.logEvent('show-pricing-detail', {});
            } else if (event.target.matches('input[type="radio"].extra-details') && event.target.checked) {
                this.logEvent('show-provider-info', {});
            }
        });
    }

    hydrateStoreData() {
        const orderflow = window.localStorage.getItem('hydrateOrderflow');
        if (orderflow) {
            this.setLoading(true);
            this.setHydrating(true);
            loadOrderFlow(false, {}, {
                flowType: 'WIZARD',
                callback: 'setFlowData',
                callbackTrigger: 'AFTER_USAGE',
                callbackClose: true,
                tariffType: 'dynamic',
                skipModal: true,
            });
        }
    }

    init() {
        this.bootstrap();
        this.addEventListeners();
        this.hydrateStoreData();
        this.logEvent('init', this.filter);
        // this.renderProducts(false);
    }

    updateFlowData(flowData) {
        const {
            sort, providers, price_per_year, contract_types,
        } = this.filter;
        this.location = { ...flowData.location };
        this.filter = { ...flowData.filter };
        this.filter.price_per_year = price_per_year ?? false;
        this.filter.provider = null;
        this.filter.sort = sort;
        this.filter.providers = providers;
        this.filter.contract_types = contract_types;

        this.renderProducts(true);
    }

    setLoading(loading) {
        this.element.classList.toggle('is-loading', loading);
    }

    setHydrating(loading) {
        this.element.classList.toggle('hydrating', loading);
    }

    animateProducts() {
        this.element.classList.add('animate-products');
    }

    updateClickListeners() {
        registerOrderFlowButtons();
        this.productContainerElement.querySelectorAll('.signup-button')
            .forEach((button) => {
                button.addEventListener('click', () => {
                    button.classList.add('loading');
                    const productData = button.dataset.product ? JSON.parse(button.dataset.product) : null;
                    this.goToSignup(productData)
                        .then(() => {
                            button.classList.remove('loading');
                        })
                        .catch((error) => {
                            button.classList.remove('loading');
                            console.error('Signup failed', error);
                        });
                });
            });
    }

    goToSignup(productData) {
        return loadSignupFlow(productData);
    }

    renderProducts(animate = false) {
        this.setLoading(true);
        this.logEvent('load-products', {});
        const apiUrl = '/ajax/dynamic-energy/render-products';
        this.filter.group_provider_products = false;
        this.filter.contract_duration = null;
        const params = new URLSearchParams();
        for (let i = 0; i < Object.entries(this.filter).length; i++) {
            const [key, value] = Object.entries(this.filter)[i];
            if (value !== null) {
                params.append(key, value);
            }
        }

        if (this.location) {
            params.append('postCode', this.location.postCode);
            params.append('number', this.location.number);
        }

        axios.get(apiUrl, {
            params,
        })
            .then((response) => {
                this.productContainerElement.innerHTML = response.data;
            })
            .then(() => {
                if (animate) {
                    this.animateProducts();
                }
            })
            .then(() => {
                this.updateClickListeners();
                this.setLoading(false);
                // this.setHydrating(false);
            })
            .catch(() => {
                // Any error should be caught in the Controller and returned as a response
                // This is just in case
                this.setLoading(false);
                this.productContainerElement.innerHTML = '<div class="card error-card">    '
                    + '<h3 class="title">Sorry, er ging iets mis</h3>\n'
                    + '<p class="subtitle-small">Onze fout! Laad de pagina opnieuw en probeer het nog een keer.</p></div>';
                this.logEvent('error-card', {});
            });
    }

    logEvent(event, eventData) {
        if (typeof window.bananas !== 'undefined') {
            window.bananas('event', `compare-dynamic-energy.${event}`, eventData);
        }
    }
}

docReady(() => {
    const comparisonElement = document.getElementById('dynamic-energy-comparer');
    window.sessionStorage.setItem('resumeOrderFlow', false);

    const comparer = new DynamicEnergyComparison(comparisonElement);
    comparer.init();
});
